"use client";

import { api } from "@convex/_generated/api";
import { useMutation } from "convex/react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface UsernameModalProps {
  open: boolean;
  onClose?: () => void;
}

export function UsernameModal({ open, onClose }: UsernameModalProps) {
  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateUsername = useMutation(api.users.mutations.updateUsername);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      toast.error("Please enter a username");
      return;
    }

    setIsSubmitting(true);

    try {
      await updateUsername({ username: username.trim() });
      toast.success("Username created successfully!");
      onClose?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create username"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow alphanumeric, underscore, and hyphen
    const sanitized = value.replace(/[^a-zA-Z0-9_-]/g, "");
    setUsername(sanitized);
  };

  return (
    <Dialog onOpenChange={onClose} open={open}>
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="font-quantico text-xl">
            Create Your Username
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            To continue using BF6 Loadouts, you need to create a username. This
            will be used to identify you in the community and for your loadout
            creations.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              className="font-mono"
              disabled={isSubmitting}
              id="username"
              maxLength={20}
              minLength={3}
              onChange={handleUsernameChange}
              placeholder="Enter your username"
              required
              type="text"
              value={username}
            />
            <p className="text-muted-foreground text-xs">
              3-20 characters. Letters, numbers, underscores, and hyphens only.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              className="min-w-[100px]"
              disabled={isSubmitting || !username.trim()}
              type="submit"
            >
              {isSubmitting ? "Creating..." : "Create Username"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
